import { Capacitor } from '@capacitor/core';
import appleIAP from '../services/appleIAP';

export function isIOS() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export function mapPlanToProductId(plan, role) {
  const name = (plan.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const appleProductId = plan.appleProductId;
  const product = plan.product;

  if (appleProductId) return appleProductId;
  if (product) return product;

  const mappings = {
    patient: {
      'basic': 'com.vivara.healthcare.patient.basic.monthlyv2',
      'premium': 'com.vivara.healthcare.patient.premium.monthlyv2',
      'gold': 'com.vivara.healthcare.patient.gold.2monthv2',
      // NOTE: ID ends in 4monthv2 but this is the 3-month plan — intentional, do not rename
      'platinum': 'com.vivara.healthcare.patient.platinum.4monthv2',
    },
    doctor: {
      '6month': 'com.vivara.healthcare.doctor.6monthv2',
      '6monthplan': 'com.vivara.healthcare.doctor.6monthv2',
      'yearly': 'com.vivara.healthcare.doctor.yearlyv2',
      '1year': 'com.vivara.healthcare.doctor.yearlyv2',
      'year': 'com.vivara.healthcare.doctor.yearlyv2',
    },
    retailer: {
      '6month': 'com.vivara.healthcare.retailer.6monthv2',
      '6monthplan': 'com.vivara.healthcare.retailer.6monthv2',
    },
    clinic: {
      'yearly': 'com.vivara.healthcare.clinic.yearlyv2',
      '1year': 'com.vivara.healthcare.clinic.yearlyv2',
      'year': 'com.vivara.healthcare.clinic.yearlyv2',
    },
    hospital: {
      'yearly': 'com.vivara.healthcare.hospital.yearlyv2',
      '1year': 'com.vivara.healthcare.hospital.yearlyv2',
      'year': 'com.vivara.healthcare.hospital.yearlyv2',
    },
  };

  const roleMappings = mappings[role] || {};
  for (const [key, id] of Object.entries(roleMappings)) {
    if (name.includes(key)) return id;
  }

  return null;
}

async function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Operation timed out after ${ms}ms`)), ms)
    ),
  ]);
}

/**
 * Format an IAP error for user-facing display.
 * BUG FIX (BUG 7): Never hide the real StoreKit error code/message.
 * The caller (UI) will see the native iOS error details.
 */
function formatIAPErrorForUser(error) {
  if (!error) return 'Purchase failed';

  // Preserve structured error data set by appleIAP.purchase()
  const parts = [];

  // Start with the main message (already formatted by appleIAP.js)
  if (error.message && error.message !== 'Purchase failed') {
    parts.push(error.message);
  }

  // Append native error code if available and not already in message
  if (error.iapCode !== undefined && !String(error.message || '').includes(String(error.iapCode))) {
    parts.push(`StoreKit code: ${error.iapCode}`);
  }

  // Append native message if different from main message
  if (error.nativeMessage && error.nativeMessage !== error.message) {
    parts.push(error.nativeMessage);
  }

  return parts.length > 0 ? parts.join(' — ') : 'Purchase failed';
}

export async function handleIOSSubscription({
  plan,
  role,
  onSuccess,
  onError,
  onFinally,
}) {
  try {
    console.log('[iapPayment] handleIOSSubscription called for plan:', plan?.name, 'role:', role);

    await withTimeout(appleIAP.initialize(), 20000);

    const productId = mapPlanToProductId(plan, role);
    if (!productId) {
      throw new Error(`No Apple product ID found for plan: ${plan.name} (role: ${role})`);
    }

    console.log('[iapPayment] Initiating purchase for productId:', productId);
    const result = await withTimeout(appleIAP.purchase(productId), 60000);

    console.log('[iapPayment] Purchase succeeded:', result);
    if (onSuccess) await onSuccess(result);
  } catch (error) {
    if (error.message === 'User cancelled') {
      console.log('[iapPayment] User cancelled the purchase — no error shown');
      return;
    }

    // BUG FIX (BUG 7): Show the real error, not just 'Purchase failed'
    const userMessage = formatIAPErrorForUser(error);
    console.error('[iapPayment] handleIOSSubscription error:', {
      message: error.message,
      iapCode: error.iapCode,
      nativeMessage: error.nativeMessage,
      productId: error.productId,
    });

    if (onError) onError(userMessage);
  } finally {
    if (onFinally) onFinally();
  }
}

export async function handleIOSPayPerUse({
  productId,
  onSuccess,
  onError,
  onFinally,
}) {
  try {
    console.log('[iapPayment] handleIOSPayPerUse called for productId:', productId);

    await withTimeout(appleIAP.initialize(), 20000);

    console.log('[iapPayment] Initiating pay-per-use purchase for:', productId);
    const result = await withTimeout(appleIAP.purchase(productId), 60000);

    console.log('[iapPayment] Pay-per-use purchase succeeded:', result);
    if (onSuccess) await onSuccess(result);
  } catch (error) {
    if (error.message === 'User cancelled') {
      console.log('[iapPayment] User cancelled the pay-per-use purchase — no error shown');
      return;
    }

    // BUG FIX (BUG 7): Show the real error, not just 'Purchase failed'
    const userMessage = formatIAPErrorForUser(error);
    console.error('[iapPayment] handleIOSPayPerUse error:', {
      message: error.message,
      iapCode: error.iapCode,
      nativeMessage: error.nativeMessage,
      productId: error.productId,
    });

    if (onError) onError(userMessage);
  } finally {
    if (onFinally) onFinally();
  }
}
